<template>
  <div
    :class="['swiper', ...(swiper_params.classes || []), {'swiper--locked': is_locked}]"
  >
    <swiper-container :ref="ref_id" init="false">
      <slot name="default"></slot>
    </swiper-container>
    <button
      v-if="!hide_controls"
      type="button"
      role="navigation"
      class="btn swiper__button swiper__button__prev"
      :class="{ 'swiper__button--disabled': is_beginning }"
      :aria-disabled="is_beginning ? 'true' : 'false'"
      :style="{
        'margin-left': `-${swiper_params.button_offset || 0}px`,
      }"
      @click="swipe(-1)"
    >
      <vb-icon
        id="prev-button"
        :name="'angle_left'"
        :size="swiper_params.controls_size || 20"
        class="arrow"
      >
        PREV
      </vb-icon>
    </button>
    <button
      v-if="!hide_controls"
      type="button"
      role="navigation"
      class="btn swiper__button swiper__button__next"
      :class="{ 'swiper__button--disabled': is_end }"
      :aria-disabled="is_end ? 'true' : 'false'"
      :style="{
        'margin-right': `-${swiper_params.button_offset || 0}px`,
      }"
      @click="swipe(1)"
    >
      <vb-icon
        id="next-button"
        :name="'angle_right'"
        :size="swiper_params.controls_size || 20"
        class="arrow"
      >
        NEXT
      </vb-icon>
    </button>
  </div>
</template>

<script>
  export default {
    name: 'swiper',
    props: {
      ref_id: { type: String, default: 'swiper:ref_id' },
      swiper_params: {
        type: Object,
        default: () => ({}),
      },
    },
    data() {
      return {
        slide_speed: 300,
        swiper_instance: {},
        hide_controls: false,
        is_locked: false,
        is_beginning: true,
        is_end: false,
      };
    },
    computed: {
      slides_count() {
        if (!this.$slots) {
          return 0;
        }
        if (!this.$slots['default']) {
          return 0;
        }
        if (!this.$slots['default'].length) {
          return 0;
        }
        return this.$slots['default'].length;
      },
    },
    mounted() {
      const swiper_instance = this.$refs[this.ref_id];
      Object.assign(swiper_instance, {
        eventsPrefix: 'swiper:',
        slidesPerView: 1,
        loop: false,
        ...this.swiper_params,
      });
      swiper_instance.initialize();
      this.swiper_instance = swiper_instance;
      this.bind_edges(swiper_instance);
      this.$emit('swiper-initialized', swiper_instance);
      this.hide_controls = this.get_hide_controls(
        swiper_instance,
        this.swiper_params,
        this.slides_count
      );
      // Opt-in, backward compatible: only when explicitly enabled, react to
      // Swiper's overflow lock state (all slides fit) to hide controls and/or
      // align the track.
      if (
        this.swiper_params.hide_controls_on_lock ||
        this.swiper_params.align_on_lock
      ) {
        this.bind_overflow_lock(swiper_instance, this.hide_controls);
      }
    },
    beforeDestroy() {
      if (this._overflow_swiper && this._overflow_handler) {
        this._overflow_swiper.off('lock', this._overflow_handler);
        this._overflow_swiper.off('unlock', this._overflow_handler);
      }
      if (this._edges_swiper && this._edges_handler) {
        this._edges_swiper.off('progress', this._edges_handler);
      }
    },
    methods: {
      // Tracks whether the track sits at either end, so the matching control
      // gets `swiper__button--disabled` (styled by consumers).
      bind_edges(el) {
        const swiper = el && el.swiper;
        if (!swiper || typeof swiper.on !== 'function') {
          return;
        }
        this._edges_swiper = swiper;
        this._edges_handler = () => {
          this.is_beginning = !!swiper.isBeginning;
          this.is_end = !!swiper.isEnd;
        };
        this._edges_handler();
        swiper.on('progress', this._edges_handler);
      },

      // React to Swiper's overflow lock state (all slides fit → no scroll).
      // Wired only when hide_controls_on_lock and/or align_on_lock are set, so
      // existing consumers are unaffected. Alignment is applied ONLY while
      // locked: when overflowing, the track stays default so it never fights
      // Swiper's scroll direction. `base_hide` preserves the breakpoint result.
      bind_overflow_lock(el, base_hide) {
        const swiper = el && el.swiper;
        if (!swiper || typeof swiper.on !== 'function') {
          return;
        }
        const { hide_controls_on_lock, align_on_lock } = this.swiper_params;
        const wrapper =
          el.shadowRoot && el.shadowRoot.querySelector('.swiper-wrapper');
        this._overflow_swiper = swiper;
        this._overflow_handler = () => {
          const locked = !!swiper.isLocked;
          this.is_locked = locked;
          if (hide_controls_on_lock) {
            this.hide_controls = base_hide || locked;
          }
          if (align_on_lock && wrapper) {
            wrapper.style.justifyContent = locked ? align_on_lock : '';
          }
        };
        this._overflow_handler();
        swiper.on('lock', this._overflow_handler);
        swiper.on('unlock', this._overflow_handler);
      },

      get_hide_controls(swiper, swiper_params, slides_count) {
        const { hide_controls = false, breakpoints = {} } = swiper_params || {};
        let hide_controls_for_breakpoint = false;
        if (!hide_controls && swiper) {
          const clientWidth = swiper.clientWidth || 0;
          hide_controls_for_breakpoint = Object.entries(breakpoints)
            .map((entry) => ({ width: Number(entry[0]), ...entry[1] }))
            .reduce((b, breakpoint) => {
              const not_enough_slides =
                clientWidth >= breakpoint.width &&
                breakpoint.slidesPerView >= slides_count;

              return b || not_enough_slides;
            }, false);
        }
        return hide_controls || hide_controls_for_breakpoint;
      },

      swipe(direction = 0) {
        if (!this.ref_id || !this.$refs) {
          return;
        }
        const { swiper } = this.$refs[this.ref_id] || {};
        if (direction === 0 || !swiper) {
          return;
        }
        const slide_speed = this.swiper_params.slide_speed || this.slide_speed;
        if (direction < 0) {
          swiper.slidePrev(slide_speed);
          return;
        }
        swiper.slideNext(slide_speed);
      },
    },
  };
</script>

<style lang="scss" scoped>
  .swiper {
    z-index: 0;
    position: relative;

    &__button {
      position: absolute;
      padding: 5px;
      top: calc((100% - 30px) / 2);
      background-color: white;
      border-radius: 100%;
      z-index: 3;
      box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.1);

      .vb-icon {
        position: relative;
      }

      &__prev {
        left: 12px;

        .vb-icon {
          left: -1px;
        }
      }

      &__next {
        right: 12px;

        .vb-icon {
          right: -1px;
        }
      }
    }
  }
</style>
